import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ShieldAlert, Trash2, KeyRound, ExternalLink, BookOpen } from "lucide-react";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SecurityIncidentsStrip({ password }: { password: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-security-overview"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-dashboard-metrics", {
        body: { action: "security_overview" },
        headers: { "x-admin-password": password },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as {
        openIncidents: number;
        retention: {
          count7d: number;
          last: { action: string; actorType: string; actorEmail: string | null; source: string | null; at: string } | null;
        };
      };
    },
  });

  const lastRetention = data?.retention.last;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Security & incidents</h2>
        <div className="flex gap-2 text-xs">
          <a
            href="https://github.com"
            onClick={(e) => {
              e.preventDefault();
              window.open("/docs/runbooks/incident-response.md", "_blank", "noreferrer");
            }}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <BookOpen className="h-3.5 w-3.5" /> Runbook
          </a>
          <a href="/security" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3.5 w-3.5" /> /security
          </a>
          <a href="/status" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3.5 w-3.5" /> /status
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Open incidents */}
        <div className="bg-card border border-border rounded-[0.875rem] p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-emerald-500/10 text-emerald-500">
              <ShieldAlert className="h-4 w-4" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Open incidents</span>
          </div>
          <div className="font-display text-2xl font-semibold">
            {isLoading ? "—" : data?.openIncidents ?? 0}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">Tracked manually in runbook</div>
        </div>

        {/* Retention actions (7d) */}
        <div className="bg-card border border-border rounded-[0.875rem] p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
              <Trash2 className="h-4 w-4" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Retention actions (7d)</span>
          </div>
          <div className="font-display text-2xl font-semibold">
            {isLoading ? "—" : data?.retention.count7d ?? 0}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {lastRetention
              ? `Last: ${lastRetention.action} · ${lastRetention.actorType} · ${relativeTime(lastRetention.at)}`
              : "No retention actions yet"}
          </div>
        </div>

        {/* Admin auth failures (link out) */}
        <a
          href="https://sentry.io"
          target="_blank"
          rel="noreferrer"
          className="bg-card border border-border rounded-[0.875rem] p-4 hover:border-primary/40 transition-colors"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-500/10 text-amber-500">
              <KeyRound className="h-4 w-4" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Admin auth failures</span>
          </div>
          <div className="font-display text-2xl font-semibold flex items-center gap-2">
            View <ExternalLink className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">401s on admin functions in Sentry</div>
        </a>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-xs rounded-lg p-3">
          {(error as Error).message}
        </div>
      )}
    </section>
  );
}